import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { AccountMenu } from '@/components/molecules/accountMenu/accountMenu'
import { useLogOutMutation } from '@/features/auth/api/auth/mutations/logout-mutation'
import { useAuth } from '@/features/auth/hooks/useAuth'
import logoBlack from '@/assets/logo-black.svg'
import logoWhite from '@/assets/logo-white.svg'
import { cn } from '@/shared/lib/utils'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'

interface AppSidebarShellProps {
  children: ReactNode
  collapsed?: boolean
  headerAction?: ReactNode
  className?: string
  panelClassName?: string
  headerClassName?: string
  accountClassName?: string
  showAccountInfo?: boolean
  mobileOpen?: boolean
  /** Product name next to the logo. */
  brand?: string
  /** Shown just above the account controls (e.g. usage). */
  footer?: ReactNode
}

export function AppSidebarShell({
  children,
  collapsed = false,
  headerAction,
  className,
  panelClassName,
  headerClassName,
  accountClassName,
  showAccountInfo = true,
  mobileOpen = true,
  brand = 'Laplace',
  footer,
}: AppSidebarShellProps) {
  const lang = useCurrentLang()
  const { user } = useAuth()
  const signOutMutation = useLogOutMutation()

  return (
    <aside
      className={cn(
        'shrink-0 border-r bg-sidebar/95 text-sidebar-foreground backdrop-blur supports-[backdrop-filter]:bg-sidebar/60',
        className,
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
      )}
    >
      <div
        className={cn(
          'flex h-full min-h-0 w-full flex-1 flex-col gap-3 px-2 py-3 transition-all duration-200',
          collapsed ? 'md:w-20' : 'md:w-80',
          panelClassName,
        )}
      >
        <div
          className={cn(
            collapsed
              ? 'flex flex-col items-center gap-2'
              : 'mb-2 flex items-center justify-between gap-2',
            headerClassName,
          )}
        >
          <Link
            to="/$lang/app"
            params={{ lang }}
            className={cn(
              'flex min-w-0 items-center gap-1.5 rounded-md text-sidebar-foreground transition-colors hover:text-foreground',
              collapsed && 'justify-center',
            )}
          >
            <img
              src={logoBlack}
              alt={brand}
              className="h-7 w-auto shrink-0 dark:hidden sm:h-8"
            />
            <img
              src={logoWhite}
              alt={brand}
              className="hidden h-7 w-auto shrink-0 dark:block sm:h-8"
            />
            {!collapsed ? (
              <span className="truncate text-base font-semibold sm:text-lg">
                {brand}
              </span>
            ) : null}
          </Link>
          {headerAction}
        </div>

        {children}

        {footer ? (
          <div className={cn('mt-auto', collapsed && 'flex justify-center')}>
            {footer}
          </div>
        ) : null}

        <div
          className={cn(
            'border-t border-sidebar-border pt-3',
            !footer && 'mt-auto',
            collapsed ? 'flex justify-center' : 'flex justify-start',
            accountClassName,
          )}
        >
          <AccountMenu
            user={user}
            onClick={() => signOutMutation.mutate()}
            align="start"
            side="top"
            showUserInfo={showAccountInfo && !collapsed}
          />
        </div>
      </div>
    </aside>
  )
}
